import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  inject,
} from '@angular/core';
import { UserManagementService } from '../user-management.service';
import {
  UntypedFormGroup,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog,
} from '@angular/material/dialog';
import { Observable } from 'rxjs';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteModule,
} from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import {
  startWith,
  map,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WarnDialogComponent } from '../../../warn-dialog/warn-dialog.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { RightBridgeApiService } from '../../../services/right-bridge-api.service';
import { AdminService } from '../../admin.service';
import { ResultString } from '../../../models/general-models';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AsyncPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-edit-user',
  templateUrl: './edit-user.component.html',
  styleUrls: ['./edit-user.component.scss'],
  imports: [
    MatSelectModule,
    MatCheckboxModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    AsyncPipe,
    ReactiveFormsModule,
    MatButtonModule,
    MatInputModule,
    MatIconModule,
  ],
})
export class EditUserComponent implements OnInit {
  dialogRef = inject<MatDialogRef<EditUserComponent>>(MatDialogRef);
  private userService = inject(UserManagementService);
  private _snackBar = inject(MatSnackBar);
  dialog = inject(MatDialog);
  private ss = inject(SessionStorageService);
  private rbs = inject(RightBridgeApiService);
  private adminService = inject(AdminService);
  data = inject(MAT_DIALOG_DATA);

  userForm = new UntypedFormGroup({
    id: new FormControl(),
    isactive: new FormControl(),
    userName: new FormControl(),
    loginName: new FormControl(),
    Title: new FormControl(),
    organization: new FormControl(),
    firstName: new FormControl(),
    lastName: new FormControl(),
    email: new FormControl(),
    phoneNumber: new FormControl(),
    saveType: new FormControl(),
    producer: new FormControl(),
    unitsManaged: new FormControl(),
    roles: new FormControl(),
    repCodes: new FormControl(),
    licenses: new FormControl(),
    proxyUsers: new FormControl(),
  });
  unitCtrl = new FormControl();
  filteredUnits = [];
  filteredRoles: Observable<string[]>;
  filteredUsers = [];
  filteredReps = [];
  units = [];
  createRepcode = true;
  proxyData = [];
  reps = [];
  repData = [];
  users = [];
  roles = [];
  roleData = [];
  roleCtrl = new FormControl();
  repCtrl = new FormControl();
  userCtrl = new FormControl();
  removable = true;
  proxySelection = [];
  allRoles = ['Administrator', 'AW Rep'];
  rights: Array<string> = [];

  separatorKeysCodes: number[] = [ENTER, COMMA];
  @ViewChild('unitInput', { static: false })
  unitInput: ElementRef<HTMLInputElement>;
  @ViewChild('roleInput', { static: false })
  roleInput: ElementRef<HTMLInputElement>;
  @ViewChild('repInput', { static: false })
  repInput: ElementRef<HTMLInputElement>;
  @ViewChild('userInput', { static: false })
  userInput: ElementRef<HTMLInputElement>;
  repDropdownLoading = false;
  repDropdownNoResults = false;
  unitDropdownLoading = false;
  unitDropdownNoResults = false;

  selectedUnit(event: MatAutocompleteSelectedEvent): void {
    this.units.push(event.option.viewValue);
    this.unitInput.nativeElement.value = '';
    this.unitCtrl.setValue(null);
  }

  constructor() {
    this.filteredRoles = this.roleCtrl.valueChanges.pipe(
      startWith(null),
      map((role: string | null) =>
        role ? this._filterItem(role, this.allRoles) : this.allRoles.slice()
      )
    );
  }

  ngOnInit() {
    this.roleData = this.data.roleData;
    this.allRoles = this.data.allRoles;
    this.rights = this.ss.get('rights') || [];

    if (this.data.user) {
      this.userForm = new UntypedFormGroup({
        id: new FormControl(this.data.user['id']),
        isactive: new FormControl(this.data.user['active']),
        userName: new FormControl(this.data.user['userName']),
        loginName: new FormControl(this.data.user['loginName']),
        Title: new FormControl(this.data.user['Title']),
        organization: new FormControl(this.data.user['organization']),
        firstName: new FormControl(this.data.user['firstName']),
        lastName: new FormControl(this.data.user['lastName']),
        email: new FormControl(this.data.user['email']),
        phoneNumber: new FormControl(this.data.user['phoneNumber']),
        saveType: new FormControl(this.data.user['saveType']),
        producer: new FormControl(+this.data.user['producer'] === 1),
        unitsManaged: new FormControl(this.data.user['unitsManaged']),
        roles: new FormControl(this.data.user['roles'].map(r => r.name)),
        repCodes: new FormControl(this.data.user['repCodes']),
        licenses: new FormControl(this.data.user['licenses']),
        proxyUsers: new FormControl(
          this.data.user['proxyUsers'].map(u => u.split('|')[0])
        ),
      });
      this.data.user['proxyUsers'].forEach(u => {
        const split = u.split('|');
        this.proxyData.push({ id: split[1], name: split[0] });
      });
      this.reps = this.data.user['repCodes'];
      this.data.user['proxyUsers'].forEach(u => {
        const split = u.split('|');
        this.users.push({ id: split[1], name: split[0] });
      });
      this.units = this.data.user['unitsManaged'];
      this.roles = this.data.user['roles'];
    }

    this.repCtrl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((rep: string | null) =>
        rep && rep.length > 1 ? this._filterReps(rep) : null
      );
    this.unitCtrl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((unit: string | null) =>
        unit && unit.length > 1 ? this._filterUnits(unit) : null
      );
  }

  cancel() {
    this.dialogRef.close();
  }

  saveUser() {
    const saveData = this.userForm.value;
    saveData['roles'] = this.roles.map(
      r => this.roleData.find(r2 => r2.name === r).id
    );
    saveData['unitsManaged'] = this.units.map(u => u.split(':')[0]);
    saveData['repCodes'] = this.reps.map(r => r.split(':')[0]);
    saveData['proxyUsers'] = this.users.map(u => {
      const proxyUserToAdd = this.proxyData.find(u2 => u2.id === u.id);
      return `${proxyUserToAdd.id}`;
    });
    saveData['producer'] = saveData['producer'] ? 1 : 0;
    this.userService.update(saveData).subscribe(() => {
      this.openSnackBar('User Saved');
      this.dialogRef.close({ response: saveData });
      this.userService
        .searchUsers(
          {
            id: '=' + saveData['id'],
            name: '',
            username: '',
            unit: '',
            repcode: '',
          },
          0
        )
        .subscribe();
    });
  }

  removeItem(user: string, users: Array<any>): void {
    const index = users.indexOf(user);

    if (index >= 0) {
      users.splice(index, 1);
    }
  }

  addItem(
    event: MatChipInputEvent,
    items: Array<any>,
    control: FormControl
  ): void {
    const input = event.input;
    const value = event.value;

    if ((value || '').trim()) {
      items.push(value.trim());
    }

    if (input) {
      input.value = '';
    }

    control.setValue(null);
  }

  focusRoles() {
    this.roleInput.nativeElement.blur();
    this.roleInput.nativeElement.focus();
  }

  focusReps() {
    this.repInput.nativeElement.blur();
    this.repInput.nativeElement.focus();
  }

  focusUnits() {
    this.unitInput.nativeElement.blur();
    this.unitInput.nativeElement.focus();
  }

  deleteUser() {
    let dialogRef = this.dialog.open(WarnDialogComponent, {
      panelClass: 'warn-dialog',
      data: {
        headline: 'Delete User?',
        content: 'Please confirm user deletion.',
        confirm: 'Delete',
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'continue') {
        const data = this.userForm.value;
        this.userService.remove(data).subscribe((response: ResultString) => {
          if (response !== null && response['result'] === 'Success') {
            this.dialogRef.close({ removedUser: data });
            this.openSnackBar('User Removed');
          }
        });
      }
      dialogRef = null;
    });
  }

  openSnackBar(message: string) {
    this._snackBar.open(message, '', {
      duration: 2000,
    });
  }

  selectedRole(event: MatAutocompleteSelectedEvent): void {
    this.roles.push(event.option.viewValue);
    this.roleInput.nativeElement.value = '';
    this.roleCtrl.setValue(null);
  }

  selectedRep(event: MatAutocompleteSelectedEvent): void {
    this.reps.push(event.option.viewValue);
    this.repInput.nativeElement.value = '';
    this.repCtrl.setValue(null);
  }

  selectedUser(event: MatAutocompleteSelectedEvent): void {
    this.addProxyData(event.option.value);
    this.users.push(event.option.value);
    this.userInput.nativeElement.value = '';
    this.userCtrl.setValue(null);
  }

  addProxyData(value) {
    const data = this.proxySelection.find(u => u['id'] === value.id);
    this.proxyData.push(data);
  }

  userLookAhead(event) {
    this.userService.advisorLookahead(event.target.value).subscribe(users => {
      this.proxySelection = users.users;
      this.filteredUsers = users.users;
    });
  }

  private _filterItem(value: string, items: Array<any>): string[] {
    const filterValue = value.toLowerCase();

    return items.filter(unit => unit.toLowerCase().indexOf(filterValue) === 0);
  }

  private _filterUnits(value: string): void {
    const filterValue = value.toLowerCase();

    this.unitDropdownNoResults = false;
    this.unitDropdownLoading = true;

    this.adminService.getUnitList(filterValue, true).subscribe(res => {
      this.filteredUnits = res.names;
      this.unitDropdownLoading = false;
      this.unitDropdownNoResults = this.filteredUnits.length === 0;
    });
  }
  private _filterReps(value: string): void {
    const filterValue = value.toLowerCase();

    this.repDropdownNoResults = false;
    this.repDropdownLoading = true;

    this.rbs.getRepCodes(filterValue).subscribe(res => {
      this.filteredReps = res.names;
      this.repDropdownLoading = false;
      this.repDropdownNoResults = this.filteredReps.length === 0;
    });
  }

  public resetPassword(): void {
    this.userService.resetPassword(this.userForm.value.id).subscribe(() => {
      this.openSnackBar('Request Sent');
    });
  }
}
